import {
  PrepaidBookingPricesMyMSCRequest,
  PrepaidBookingPricesMyMSCResponse,
  PrepaidBookingPricesMyMSCUrlParams,
} from '@msc-b2c/interfaces/interfaces';
import { CartItem, PricesResponseItem } from '@msc-b2c/interfaces/types';
import { ErrorManagerCodeEnum } from '@msc-b2c/interfaces/enums';
import { isCartAvailabilityErrorCode } from './cartAvailabilityUtils';
import { getExcursionsFromCart, getObsFromCart } from '../../utils/myAreaCartUtils';
import {
  AppDispatch,
  closeLoader,
  GetExcursionsMyMSC,
  GetOnboardServicesByCruiseIdMyMSC,
  isChain2,
  isProduction,
  myAreaExcursions,
  myAreaOBS,
  openLoader,
  PrepaidBookingPricesCruisesMyMSC,
  RootState,
  setErrorManagerCode,
  store,
  updateCartItems,
  updateCartPrices,
} from '@msc-b2c/shared-utils';

type GetPricesCartOptions = {
  handleAvailabilityErrors?: boolean;
  onPricesError?: (issueCode?: string) => void;
  onCartAvailabilityError?: (issueCode: string) => void;
};

const normalizeCode = (code?: string): string => code?.trim().toLowerCase() ?? '';

const extractIssueCodeFromError = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const maybeError = error as {
    issue_code?: string;
    code?: string;
    response?: { data?: { issue_code?: string; code?: string } };
  };

  return (
    maybeError.issue_code ??
    maybeError.code ??
    maybeError.response?.data?.issue_code ??
    maybeError.response?.data?.code
  );
};

const reconcileCartItemsWithPrices = (response: PrepaidBookingPricesMyMSCResponse): CartItem[] => {
  const currentCartItems = store.getState().myAreaCart.items;
  const availableExcursionCodes = new Set((response.excursions || []).map((item) => item.code));
  const availableObsCodes = new Set((response.onboardServices || []).map((item) => item.code));

  return currentCartItems.filter((item) => {
    if (item.itemType === 'EXCURSION') {
      return availableExcursionCodes.has(item.code);
    }
    return availableObsCodes.has(item.code);
  });
};

const refreshPrepaidResults = (
  dispatch: AppDispatch,
  cruiseId: string,
  bookingNumber: string
): void => {
  const state = store.getState() as unknown as RootState;
  const visibleExcursionCodes = new Set(
    state.myCruise.excursions?.excursions?.map((excursion) => excursion.code).filter(Boolean) ?? []
  );
  const visibleObsCodes = new Set(
    state.myCruise.obs?.onboardServices?.map((obs) => obs.code).filter(Boolean) ?? []
  );
  const includeDepartureAndArrivalPorts =
    !state.myAreaCommonConfig.prePaidCardProps.disableEmbarkAndDisembarkExcursions;

  const getExcursionsData = new GetExcursionsMyMSC(false, {
    cruiseId,
    bookingNumber,
    includeDepartureAndArrivalPorts,
  });

  getExcursionsData
    .getData()
    .then((res) => {
      if (visibleExcursionCodes.size > 0) {
        res.excursions = res.excursions.filter((excursion) =>
          visibleExcursionCodes.has(excursion.code)
        );
      }
      dispatch(myAreaExcursions(res));
    })
    .catch((error) => {
      console.error('Error refreshing excursions after cart availability error:', error);
    });

  const getObsData = new GetOnboardServicesByCruiseIdMyMSC(false, {
    cruiseID: cruiseId,
    bookingNumber,
  });

  getObsData
    .getData()
    .then((res) => {
      if (visibleObsCodes.size > 0) {
        res.onboardServices = res.onboardServices?.filter((obs) => visibleObsCodes.has(obs.code));
      }
      dispatch(myAreaOBS(res));
    })
    .catch((error) => {
      console.error('Error refreshing OBS after cart availability error:', error);
    });
};

export const getPricesCart = (
  dispatch: AppDispatch,
  cruiseId: string,
  bookingNumber: string,
  skipPromoError = false,
  couponCodeToValidate?: string,
  options: GetPricesCartOptions = {}
): Promise<PrepaidBookingPricesMyMSCResponse> => {
  const { handleAvailabilityErrors = true, onPricesError, onCartAvailabilityError } = options;

  const urlParams: PrepaidBookingPricesMyMSCUrlParams = {
    cruiseId: cruiseId || '',
  };
  const excursionsInCart = getExcursionsFromCart();
  const obsInCart = getObsFromCart();
  const appliedECoupon = store.getState().myAreaCart.appliedECoupon;
  const couponCodeToApply = couponCodeToValidate ?? appliedECoupon;
  const prepaidBookingPricesMyMSCRequestData: PrepaidBookingPricesMyMSCRequest = {
    bookingNumber: bookingNumber,
    ...(couponCodeToApply && { coupons: [{ code: couponCodeToApply }] }),
    returnPath:
      (isProduction() || isChain2() ? '' : window.location.origin) +
      `${window.location.pathname}-paymentredirection${window.location.search}`,
    ...(excursionsInCart.length > 0 && { excursions: excursionsInCart }),
    ...(obsInCart.length > 0 && { onboardServices: obsInCart }),
  };

  const prepaidBookingPrices = new PrepaidBookingPricesCruisesMyMSC(
    false,
    false,
    prepaidBookingPricesMyMSCRequestData,
    urlParams
  );

  console.info('Prices request data:', prepaidBookingPricesMyMSCRequestData);

  openLoader('.loader');

  return prepaidBookingPrices
    .sendData()
    .then((res) => {
      const cartItemsBeforeReconcile = store.getState().myAreaCart.items;
      const reconciledItems = reconcileCartItemsWithPrices(res);

      dispatch(updateCartPrices(res));
      dispatch(updateCartItems(reconciledItems));

      const warningCode = normalizeCode(res?._warnings?.[0]?.code);
      let availabilityIssueCode: string | undefined;

      if (isCartAvailabilityErrorCode(warningCode)) {
        availabilityIssueCode = warningCode;
      } else if (reconciledItems.length === 0 && cartItemsBeforeReconcile.length > 0) {
        availabilityIssueCode = ErrorManagerCodeEnum.ALL_ITEMS_NOT_AVAILABLE;
      } else if (reconciledItems.length < cartItemsBeforeReconcile.length) {
        availabilityIssueCode = ErrorManagerCodeEnum.SOME_ITEMS_NOT_AVAILABLE;
      }

      if (availabilityIssueCode) {
        refreshPrepaidResults(dispatch, cruiseId, bookingNumber);
        if (onCartAvailabilityError) {
          onCartAvailabilityError?.(availabilityIssueCode);
        } else {
          dispatch(setErrorManagerCode(availabilityIssueCode));
        }
      } else if (handleAvailabilityErrors && warningCode) {
        dispatch(setErrorManagerCode(warningCode));
      }

      console.info('Prepaid booking prices response:', res);
      const hadInvalidPromo = checkPromoValidityInCart(
        dispatch,
        prepaidBookingPricesMyMSCRequestData,
        res,
        skipPromoError
      );
      // A no-longer-available promotion corrects the cart price but leaves the stale promo price on
      // the result cards; refresh the catalog so cards match the recalculated price (skip if an
      // availability issue already triggered a refresh above).
      if (hadInvalidPromo && !availabilityIssueCode) {
        refreshPrepaidResults(dispatch, cruiseId, bookingNumber);
      }
      return res;
    })
    .catch((error: any) => {
      console.error('Error fetching prepaid booking prices:', error);
      const issueCode = normalizeCode(extractIssueCodeFromError(error));

      if (issueCode === ErrorManagerCodeEnum.ALL_ITEMS_NOT_AVAILABLE) {
        dispatch(updateCartItems([]));
        dispatch(updateCartPrices(null));
      }

      if (isCartAvailabilityErrorCode(issueCode)) {
        refreshPrepaidResults(dispatch, cruiseId, bookingNumber);
        if (onCartAvailabilityError) {
          onCartAvailabilityError?.(issueCode);
        } else {
          dispatch(setErrorManagerCode(issueCode));
        }
      } else if (handleAvailabilityErrors) {
        dispatch(setErrorManagerCode(issueCode || 'unknown_error'));
      }

      onPricesError?.(issueCode);

      throw error;
    })
    .finally(() => {
      closeLoader('.loader');
    });
};

const hasPositivePromoAmount = (amount?: string): boolean => Number(amount || 0) > 0;

/**
 * Successful prices response (HTTP 200): request had a promotion id that the response no longer
 * recognizes (not echoed on item passengers/cabins, and no positive promo amount).
 * A zero promotionAmount alone is NOT invalid — e.g. minors can keep a recognized promo id with €0.
 * Triggers Error Manager with invalid_promotion_id (AEM rule), then strips invalid promotions from cart.
 * Returns true when at least one promotion was found invalid and stripped, so callers can refresh the
 * results catalog (the cards keep the stale promo price otherwise until a full page reload).
 */
export const checkPromoValidityInCart = (
  dispatch: AppDispatch,
  request: PrepaidBookingPricesMyMSCRequest,
  response: PrepaidBookingPricesMyMSCResponse,
  skipPromoError = false
): boolean => {
  const isInvalidPromoItem = (
    reqItem: { code: string; promotion?: { id: string } },
    responseItems: PricesResponseItem[] | undefined
  ): boolean => {
    const requestedPromoId = reqItem.promotion?.id;
    if (!requestedPromoId) return false;

    const apiItem = responseItems?.find((r) => r.code === reqItem.code);
    // Missing item is handled by availability reconciliation, not promo-loss.
    if (!apiItem) return false;

    if (hasPositivePromoAmount(apiItem.price?.total?.promotionAmount)) {
      return false;
    }

    const promoStillRecognizedOnPassengers = apiItem.passengers?.some(
      (passenger) =>
        passenger.promotion?.id === requestedPromoId ||
        hasPositivePromoAmount(passenger.price?.total?.promotionAmount)
    );
    if (promoStillRecognizedOnPassengers) {
      return false;
    }

    const promoStillRecognizedOnCabins = apiItem.cabins?.some(
      (cabin) =>
        cabin.promotion?.id === requestedPromoId ||
        hasPositivePromoAmount(cabin.price?.total?.promotionAmount)
    );
    if (promoStillRecognizedOnCabins) {
      return false;
    }

    // Requested promo was not applied and not echoed back → truly unavailable.
    return true;
  };

  const invalidExcursionPromoItems =
    request.excursions?.filter((reqItem) => isInvalidPromoItem(reqItem, response.excursions)) || [];

  const invalidObsPromoItems =
    request.onboardServices?.filter((reqItem) =>
      isInvalidPromoItem(reqItem, response.onboardServices)
    ) || [];

  if (invalidExcursionPromoItems.length + invalidObsPromoItems.length === 0) {
    return false;
  }

  if (!skipPromoError) {
    dispatch(setErrorManagerCode(ErrorManagerCodeEnum.INVALID_PROMOTION_ID));
  }

  const cart = store.getState().myAreaCart.items;
  const cleanedCart = cart.map((item) => {
    if (invalidExcursionPromoItems.some((inv) => inv.code === item.code)) {
      return { ...item, promotion: undefined };
    }
    if (invalidObsPromoItems.some((inv) => inv.code === item.code)) {
      return { ...item, promotion: undefined };
    }
    return item;
  });

  dispatch(updateCartItems(cleanedCart));
  return true;
};
